import { FindBestPriceSelect, useGetURLQuery } from "capitalrx-components-2.0";
import cn from "classnames";
import { UrlQueryParams } from "@/api/interfaces/QueryParams";
import { useAlert, Tag } from "capitalrx-components-2.0/lib/general";
import { Form, Textbox, NumberTextbox, SubmitButton, useForm } from "capitalrx-components-2.0/lib/forms";
import { Grid, useResponsive } from "capitalrx-components-2.0/lib/layout";
import inflection from "inflection";
import { useCallback, useContext, useEffect, useState } from "react";
import { useWatch } from "react-hook-form";
import Skeleton from "react-loading-skeleton";
import { useNavigate } from "react-router";
import { useGetDrugDetailsQuery } from "@/api/getDrug";
import ErrorSection from "@/components/ErrorSection";
import { AccordianExpand, DetailParameters, FormData, Titles } from "@/api/interfaces/FindBestPrice";
import { PriceList } from "@/constants/routes";
import { ThemeSelectorContext } from "@/context/index";
import { convertToNumber } from "@/utils/convertToNumber";
import BackButton from "@/components/BackButton/BackButton";
import FindBestPriceAccordion from "../FindBestPriceAccordion";
import GenericFlag from "../FindBestPriceGenericFlag/FindBestPriceGenericFlag";

import styles from "./FindBestPriceDetails.module.scss";

// XXX: this whole thing should be refactored
const FindBestPriceDetails = (): JSX.Element => {
  const navigate = useNavigate();
  const showAlert = useAlert();
  const { client } = useContext(ThemeSelectorContext);
  const { columns } = useResponsive({
    columns: {
      desktop: 8,
      tablet: 12,
      mobile: 12,
    },
  });
  const form = useForm({
    defaultValues: {
      location: "",
      drug_id: "",
      dosage: "",
      dosage_form: "",
      quantity: "",
      find_best_price_custom_quantity: 1,
    },
  });

  const failureHandler = useCallback(
    (err: string | Error): void => {
      showAlert({ type: "error", message: typeof err === "string" ? err : err.message, style: "banner" });
    },
    [showAlert],
  );

  const getURLQuery = useGetURLQuery<UrlQueryParams>(failureHandler);
  const { drug_id, dosage, dosage_form, quantity, zipcode, group_number } = getURLQuery;
  let drug_id_number = parseInt(drug_id);

  const { data: details, refetch, isLoading, isError } = useGetDrugDetailsQuery({ drug_id: drug_id_number });

  const [dosagePackaging, setDosagePackaging] = useState("");
  const [showGenericFlag, setShowGenericFlag] = useState(true);
  const [expandedTitles, setExpandedTitles] = useState<AccordianExpand>({
    Type: null,
    Form: null,
    Dosage: null,
    Quantity: null,
  });
  const [titles, setTitles] = useState<Titles>({
    Type: "",
    Form: "",
    Dosage: "",
    Quantity: "",
  });
  const [selectorOptions, setSelectorOptions] = useState<FormData>({
    Type: [],
    Form: [],
    Dosage: [],
    Quantity: [],
  });

  if (isError) {
    showAlert({
      type: "error",
      message: "No drug information available for search. Please return to search page and try again.",
      style: "banner",
    });
  }

  // get drug details if user selects a different drug from Types
  const getAlternativeDrugId = (drugName: string) => {
    const altDrugName = details?.alternatives?.find((altDrug) => {
      if (altDrug.drug.drug_name === drugName) {
        return altDrug;
      }
    });

    return altDrugName.drug.id;
  };

  // create list of alternative drugs
  const getDrugAlternativesArray = useCallback((): string[][] => {
    const drugFormArray = [
      ...new Set(
        details?.alternatives?.map((option) => {
          return [option.drug.drug_name, option.drug.drug_type];
        }),
      ),
    ];

    return drugFormArray;
  }, [details?.alternatives]);

  // create unique list of possible forms of selected drug
  const getDrugFormArray = useCallback((): string[] => {
    const drugFormArray = [...new Set(details?.drug?.configurations?.map((option) => option.dosage_form))];

    return drugFormArray;
  }, [details?.drug?.configurations]);

  // create list of possible dosages based on selected form of selected drug
  const getDrugDosageArray = useCallback(
    (drugForm: string): string[] => {
      let drugDosageArray = [
        ...new Set(
          details?.drug?.configurations
            ?.filter((options) => {
              if (options.dosage_form === drugForm) {
                return options;
              }
            })
            .map((option) => option.dosage),
        ),
      ];

      // sort dosage by leading number
      drugDosageArray = drugDosageArray.sort((a, b) => {
        const splitA = a.split(" ");
        const splitB = b.split(" ");

        return convertToNumber(splitA[0]) - convertToNumber(splitB[0]);
      });

      return drugDosageArray;
    },
    [details?.drug?.configurations],
  );

  // get packaging form for selected drug and drug form
  const getDrugDosagePackaging = useCallback(
    (drugForm: string): string => {
      const drugDosagePackaging = details?.drug?.configurations?.find((options) => {
        if (options.dosage_form === drugForm) {
          return options;
        }
      });

      if (!drugDosagePackaging?.dosage_form_packaging) {
        return drugDosagePackaging?.dosage_form;
      }

      return drugDosagePackaging?.dosage_form_packaging;
    },
    [details?.drug?.configurations],
  );

  // create list of possible quantities based on selected form and dosage of selected drug
  const getDrugQuantityArray = useCallback(
    (drugForm: string, drugDosage: string): string[] => {
      const drugQuantitiesObject = details?.drug?.configurations?.find((options) => {
        if (options.dosage_form === drugForm && options.dosage.includes(drugDosage)) {
          return options;
        }
      });
      return drugQuantitiesObject?.display_quantities || [];
    },
    [details?.drug?.configurations],
  );

  // get default dosage because most_common_dosage isn't always the full text
  const defaultDosage = useCallback((): string => {
    const commonDosage = details?.drug?.most_common_dosage;

    const defaultDosage = details?.drug?.configurations?.find((options) => {
      if (options.dosage_form === details?.drug?.most_common_form && options.dosage.includes(commonDosage)) {
        return options;
      }
    });

    return defaultDosage?.dosage;
  }, [details?.drug?.configurations, details?.drug?.most_common_dosage, details?.drug?.most_common_form]);

  // calculate default displayed quantity
  const defaultDisplayQuantity = useCallback((): number => {
    if (details?.drug?.most_common_quantity_dispensed) {
      const commonQuantity = parseInt(details?.drug?.most_common_quantity_dispensed);

      const drugQuantitiesMultiplier = details?.drug?.configurations?.find((options) => {
        if (options.dosage_form === details?.drug?.most_common_form && options.dosage.includes(details?.drug?.most_common_dosage)) {
          return options;
        }
      });

      return Math.round(commonQuantity / drugQuantitiesMultiplier?.quantity_multiplier);
    }

    return parseInt(details?.drug?.configurations[0]?.display_quantities[0]);
  }, [
    details?.drug?.most_common_quantity_dispensed,
    details?.drug?.configurations,
    details?.drug?.most_common_form,
    details?.drug?.most_common_dosage,
  ]);

  const genericAlternative = useCallback(() => {
    details?.drug?.generic_alternative ? setShowGenericFlag(true) : setShowGenericFlag(false);
  }, [details?.drug?.generic_alternative]);

  useEffect(() => {
    // set default values
    const commonDosage = defaultDosage();

    let commonQuantity;
    if (quantity) {
      commonQuantity = parseInt(quantity);
    } else {
      commonQuantity = defaultDisplayQuantity();
    }

    let drugDefaultForm = "";
    if (dosage_form) {
      drugDefaultForm = dosage_form;
    } else {
      if (details?.drug?.most_common_form) {
        drugDefaultForm = details?.drug?.most_common_form;
      } else {
        drugDefaultForm = details?.drug?.configurations[0]?.dosage_form;
      }
    }

    let drugDefaultDosage = "";
    if (dosage) {
      drugDefaultDosage = dosage;
    } else {
      if (details?.drug?.most_common_form) {
        drugDefaultDosage = commonDosage;
      } else {
        drugDefaultDosage = details?.drug?.configurations[0]?.dosage;
      }
    }

    // XXX: When there isnt a found matching display quantity fallback to the first selectable option
    if (!commonQuantity) {
      commonQuantity = Number(details?.drug?.configurations[0]?.display_quantities[0]);
    }

    const dosagePackagingVariable = getDrugDosagePackaging(drugDefaultForm);
    const displayQuantity = inflection.inflect(`${commonQuantity} ${dosagePackagingVariable}`, commonQuantity);

    const defaults = {
      Type: details?.drug?.drug_type,
      Form: drugDefaultForm,
      Dosage: drugDefaultDosage,
      Quantity: displayQuantity,
    };

    setTitles(defaults);

    // set options for selected drug
    const drugAlternativesArray = getDrugAlternativesArray();
    const drugFormArray = getDrugFormArray();
    const drugDosageArray = getDrugDosageArray(defaults.Form);
    setDosagePackaging(dosagePackagingVariable);
    const drugQuantitiesArray = getDrugQuantityArray(defaults.Form, defaults.Dosage);
    genericAlternative();

    setSelectorOptions({
      Type: drugAlternativesArray,
      Form: drugFormArray,
      Dosage: drugDosageArray,
      Quantity: drugQuantitiesArray,
    });
  }, [
    details,
    dosage,
    dosage_form,
    quantity,
    getDrugAlternativesArray,
    defaultDosage,
    getDrugDosageArray,
    defaultDisplayQuantity,
    getDrugFormArray,
    getDrugQuantityArray,
    getDrugDosagePackaging,
    genericAlternative,
  ]);

  const formInputHandler = (name: string, value: string) => {
    const options = {
      ...titles,
      [name]: value,
    };

    // when alternate drug selected, update all details
    if (name === "Type") {
      drug_id_number = getAlternativeDrugId(value);
      const searchParams = new URLSearchParams();
      searchParams.append("drug_id", drug_id_number.toString());

      if (group_number) {
        searchParams.append("group_number", group_number);
      }
      navigate({
        search: encodeURIComponent(searchParams.toString()),
      });

      refetch;
    }

    // when selected form of selected drug changes, update dosage and quantity options
    if (name === "Form") {
      const drugDosageArray = getDrugDosageArray(value);
      // if selected form is not most common, set first values as default
      if (details?.drug?.most_common_form !== value) {
        options.Dosage = drugDosageArray[0];
      } else {
        const matchIndex = drugDosageArray.findIndex((dosage) => dosage.includes(details?.drug?.most_common_dosage));
        if (matchIndex >= 0) {
          options.Dosage = drugDosageArray[matchIndex];
        }
      }

      const dosagePackagingVariable = getDrugDosagePackaging(options.Form);
      const drugQuantitiesArray = getDrugQuantityArray(options.Form, options.Dosage);
      options.Quantity = inflection.inflect(`${drugQuantitiesArray[0]} ${dosagePackagingVariable}`, parseInt(drugQuantitiesArray[0]));
      setSelectorOptions({
        ...selectorOptions,
        Dosage: drugDosageArray,
        Quantity: drugQuantitiesArray,
      });

      setDosagePackaging(dosagePackagingVariable);
    }

    if (name === "Quantity") {
      const dosagePackagingVariable = getDrugDosagePackaging(options.Form);
      const displayQuantity = inflection.inflect(`${value} ${dosagePackagingVariable}`, parseInt(options.Quantity));

      options.Quantity = displayQuantity;
    }

    setTitles(options);
    resetAccordionOnSelect(name);
  };

  const genericAlternativeHandler = (genericName: string, genericId: string) => {
    const options = {
      ...titles,
      Type: genericName,
    };
    const searchParams = new URLSearchParams();
    searchParams.append("drug_id", genericId);

    if (group_number) {
      searchParams.append("group_number", group_number);
    }

    navigate({
      search: encodeURIComponent(searchParams.toString()),
    });

    refetch;

    setTitles(options);
  };

  const customQuantityValue = useWatch({
    control: form.control,
    name: "find_best_price_custom_quantity",
    defaultValue: 1,
  });

  useEffect(() => {
    if (customQuantityValue && customQuantityValue >= 1) {
      const customQuantityPackaging = getDrugDosagePackaging(titles?.Form);
      const displayQuantity = inflection.inflect(
        `${customQuantityValue} ${customQuantityPackaging}`,
        customQuantityValue,
      );

      setTitles((prevTitles) => ({
        ...prevTitles,
        Quantity: `Custom QTY: ${displayQuantity}`,
      }));
    }
  }, [customQuantityValue, titles?.Form, getDrugDosagePackaging]);

  // set parameters and go to pharmacy list page
  const submitDrugDetails = async (formData: DetailParameters): Promise<void> => {
    if (titles.Quantity.includes("Custom") && (!customQuantityValue || customQuantityValue <= 0)) {
      return;
    }

    const location = formData["location"];
    const searchParams = new URLSearchParams();
    const cleanQuantity = customQuantityValue ? parseInt(customQuantityValue.toString()) : 1;

    const drugId = details?.drug?.id.toString();
    const drugName = details?.drug?.drug_name;
    const dosage = titles?.Dosage;
    const zipCode = location;
    let quantity = null;

    searchParams.append("drug_id", drugId);
    searchParams.append("drug_name", drugName);
    searchParams.append("dosage", dosage);
    searchParams.append("dosage_form", titles?.Form);
    searchParams.append("zipcode", zipCode);

    if (titles.Quantity.includes("Custom")) {
      searchParams.append("quantity", inflection.inflect(`${cleanQuantity} ${dosagePackaging}`, cleanQuantity));
      quantity = cleanQuantity.toString();
    } else {
      searchParams.append("quantity", titles?.Quantity);
      quantity = titles?.Quantity;
    }

    if (group_number) {
      searchParams.append("group_number", group_number);
    }

    if (drugId && drugName && dosage && zipCode && quantity) {
      if (client) {
        navigate({
          pathname: `/client/${client}${PriceList}`,
          search: encodeURIComponent(searchParams.toString()),
        });
      } else {
        navigate({
          pathname: PriceList,
          search: encodeURIComponent(searchParams.toString()),
        });
      }
    } else {
      showAlert({
        type: "error",
        message: "No drug information available for search. Please return to search page and try again.",
        style: "banner",
      });
    }
  };

  const closeFlagMobile = () => {
    setShowGenericFlag(false);
  };

  const resetAccordionOnSelect = (title: string) => {
    setExpandedTitles({
      ...expandedTitles,
      [title]: (expandedTitles[title] = false),
    });

    setTimeout(() => {
      setExpandedTitles({
        ...expandedTitles,
        [title]: (expandedTitles[title] = null),
      });
    }, 100);
  };

  const loadingSkeleton = <Skeleton count={1} />;

  if (isError) {
    return <ErrorSection />;
  }

  const wrapperDivClassname = cn({
    [styles.FindBestPriceDetailsWhitelabel]: !!client,
    [styles.FindBestPriceDetailsBrandTagWhitelabel]: !!client,
  });

  const submitButtonClassname = cn(styles.FindBestPriceDetailsSubmit, { [styles.WhitelabelButton]: !!client });

  return (
    <div className={styles.FindBestPriceDetails} data-testid={details?.drug?.drug_name}>
      <Grid testId="find-best-price-details-grid">
        <Grid.Span columns={2} />
        <Grid.Spread columns={columns}>
          <div className={styles.FindBestPriceDetailsHeader}>
            <BackButton classNames={styles.FindBestPriceDetailsBack} />
            <span className="sectionTitle" test-id="find-best-price-title">
              Find the Best Price
            </span>
          </div>
        </Grid.Spread>
      </Grid>
      <Grid style={{ marginTop: "2rem" }}>
        <Grid.Span columns={2} />
        <Grid.Spread columns={columns}>
          {isLoading ? (
            loadingSkeleton
          ) : (
            <div className={styles.FindBestPriceDetailsDrugName}>
              <span className={styles.FindBestPriceDetailsSelectedDrug} data-testid="find-best-price-drug-name">
                {details?.drug?.drug_name}
              </span>
              <Tag data-testid="find-best-price-selected-drug-type">
                {details?.drug?.drug_type}
              </Tag>
            </div>
          )}
        </Grid.Spread>
      </Grid>
      <Grid>
        <Grid.Span columns={2} />
        <Grid.Spread columns={columns}>
          <Form form={form} onSubmit={submitDrugDetails} testId="find-best-price-details-form">
            <div className={wrapperDivClassname}>
              <FindBestPriceAccordion
                disableExpand={!selectorOptions?.Type.length}
                isLoading={isLoading}
                label="Type"
                title={titles.Type}
                expand={expandedTitles["Type"]}
                testId="find-best-price-drug-type"
              >
                {selectorOptions?.Type.map((option) => (
                  <FindBestPriceSelect
                    selected={titles["Type"]}
                    key={option[0]}
                    label="Type"
                    drugType={option[1]}
                    inputData={option[0]}
                    inputHandler={formInputHandler}
                    testId={`drug-type-select-${option}`}
                  />
                ))}
              </FindBestPriceAccordion>
              {showGenericFlag ? (
                <GenericFlag
                  genericList={details?.drug?.generic_alternative}
                  genericClickHandler={genericAlternativeHandler}
                  closeClickHandler={closeFlagMobile}
                />
              ) : null}
            </div>

            <div className={wrapperDivClassname}>
              <FindBestPriceAccordion
                disableExpand={selectorOptions?.Form.length <= 1}
                isLoading={isLoading}
                label="Form"
                title={titles.Form}
                expand={expandedTitles["Form"]}
                testId="find-best-price-drug-form"
              >
                {selectorOptions?.Form.map((option) => (
                  <FindBestPriceSelect
                    selected={titles["Form"]}
                    key={option}
                    label="Form"
                    inputData={option}
                    inputHandler={formInputHandler}
                    testId={`drug-form-select-${option}`}
                  />
                ))}
              </FindBestPriceAccordion>
            </div>

            <div className={wrapperDivClassname}>
              <FindBestPriceAccordion
                disableExpand={selectorOptions?.Dosage.length <= 1}
                isLoading={isLoading}
                label="Dosage"
                title={titles.Dosage}
                expand={expandedTitles["Dosage"]}
                testId="find-best-price-drug-dosage"
              >
                {selectorOptions?.Dosage.map((option) => (
                  <FindBestPriceSelect
                    selected={titles["Dosage"]}
                    key={option}
                    label="Dosage"
                    inputData={option}
                    inputHandler={formInputHandler}
                    testId={`drug-dosage-select-${option}`}
                  />
                ))}
              </FindBestPriceAccordion>
            </div>

            <div className={wrapperDivClassname}>
              <FindBestPriceAccordion
                disableExpand={selectorOptions?.Quantity.length <= 1}
                isLoading={isLoading}
                label="Quantity"
                title={titles.Quantity}
                expand={expandedTitles["Quantity"]}
                testId="find-best-price-drug-quantity"
              >
                <div className={styles.FindBestPriceDetailsCustomQuantity}>
                  <NumberTextbox.Field
                    label="Custom QTY"
                    style={{ display: "flex" }}
                    name="find_best_price_custom_quantity"
                    min={1}
                    defaultValue={1}
                    testId="find-best-price-drug-custom-quantity"
                  />
                </div>
                {selectorOptions?.Quantity.map((option) => {
                  const packaging = inflection.inflect(`${dosagePackaging}`, +option);
                  return (
                    <FindBestPriceSelect
                      selected={titles["Quantity"]}
                      key={option}
                      label="Quantity"
                      packaging={packaging}
                      inputData={option}
                      inputHandler={formInputHandler}
                      testId={`drug-quantity-select-${option}`}
                    />
                  );
                })}
              </FindBestPriceAccordion>
            </div>

            <div className={wrapperDivClassname}>
              <FindBestPriceAccordion
                isLoading={isLoading}
                label="location"
                title=""
                expanded={true}
                testId="find-best-price-drug-location"
              >
                <div className={styles.FindBestPriceDetailsLocation}>
                  <FindBestPriceSelect label="location">
                    <Textbox.Field
                      name="location"
                      placeholder="Enter zip code"
                      label="Zip Code"
                      required
                      defaultValue={zipcode ? zipcode : null}
                      testId="find-best-price-drug-location-input"
                    />
                  </FindBestPriceSelect>
                </div>
              </FindBestPriceAccordion>
            </div>

            <SubmitButton
              icon="arrow-right"
              iconPosition="right"
              className={submitButtonClassname}
              testId="find-best-price-drug-form-submit"
            >
              Calculate Price
            </SubmitButton>
          </Form>
        </Grid.Spread>
      </Grid>
    </div>
  );
};

export default FindBestPriceDetails;
